import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '../../lib/cn';

/**
 * Switch — shadcn idiom on `@radix-ui/react-switch`, themed onto HofSync brand
 * tokens. Radix renders a `<button role="switch">` (no native UA control), so
 * there is no UA chrome to reset — but because preflight is excluded we still set
 * the track surface / focus ring explicitly and paint the ON state with the
 * ACKERDOKU brand (`tw:data-[state=checked]:bg-primary`); the thumb slides on the
 * Radix `data-state` attribute.
 *
 * `@radix-ui/react-switch` is a real `dependency` (the DS is consumed as source),
 * vendored under the copy-in / ownership policy in CLAUDE.md. Chosen over Checkbox
 * for this additive slice because a legacy plain-CSS `Checkbox` molecule already
 * occupies that name in the public API (Switch is genuinely collision-free).
 */
export type SwitchProps = React.ComponentPropsWithoutRef<
  typeof SwitchPrimitive.Root
>;

export const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      'tw:peer tw:inline-flex tw:h-6 tw:w-11 tw:shrink-0 tw:cursor-pointer tw:items-center tw:rounded-full tw:border-2 tw:border-transparent tw:bg-input tw:transition-colors',
      'tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
      'tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
      'tw:data-[state=checked]:bg-primary',
      className
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        'tw:pointer-events-none tw:block tw:h-5 tw:w-5 tw:rounded-full tw:bg-card tw:shadow-sm tw:transition-transform',
        'tw:data-[state=unchecked]:translate-x-0 tw:data-[state=checked]:translate-x-5'
      )}
    />
  </SwitchPrimitive.Root>
));
Switch.displayName = 'Switch';
